import * as React from 'react';
import { MAX_IMAGE_BYTES } from '@noor/lib';
import { cn } from '@noor/lib/cn';
import { DropzoneEmptyFace, DropzoneFilledFace } from './dropzone-faces';

export interface ImageUploadProps {
  /**
   * Image to display — a server URL or a caller-owned blob: URL. `undefined`
   * renders the empty dropzone face.
   */
  imageUrl: string | undefined;
  /**
   * Upload in flight: dims and disables the control, closes the filled-state
   * menu, and swaps the browse action to `uploadingLabel`.
   */
  uploading?: boolean;
  /** Dropzone title (20px semibold). */
  title?: React.ReactNode;
  /** Format hint under the title (14px regular). */
  description?: React.ReactNode;
  /** Brand-blue browse action label. */
  browseLabel?: React.ReactNode;
  /** Prompt shown while `uploading` (replaces `browseLabel`). */
  uploadingLabel?: string;
  /** Filled-state menu: replace-image action label. */
  editLabel?: string;
  /** Filled-state menu: delete-image action label (also the ⋮ button name). */
  removeLabel: string;
  /**
   * Optional caption above the control, for callers outside a `FormField`
   * (`FormField` callers use `FormField.Label` instead).
   */
  label?: React.ReactNode;
  accept?: string;
  /**
   * Size ceiling per file, in bytes. Picks over the ceiling are rejected and
   * reported via `onRejected`; the current image is left untouched.
   */
  maxBytes?: number;
  /** Names of files rejected for exceeding `maxBytes`. */
  onRejected?: (fileNames: string[]) => void;
  disabled?: boolean;
  /** Native input id (label association). */
  id?: string;
  /** `aria-describedby` hint id for the dropzone button. */
  describedBy?: string;
  /** Invalid state (paints the error border). */
  invalid?: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
  className?: string;
  /**
   * Face geometry. Defaults to the dashed dropzone; `tile` renders the
   * compact 148px gray logo tile (Noor-CPanel merchant dialog). The
   * `description`/`browseLabel` copy is hidden in the tile (the file input
   * keeps `browseLabel` as its accessible name). `banner` is the same tile at
   * 104px tall (Noor-CPanel banner dialog).
   */
  variant?: 'default' | 'tile' | 'banner';
}

/**
 * Single-image upload (Figma 732:18311 empty, 755:24553 filled) — the URL-first
 * sibling of `Dropzone`: the caller owns the displayed URL (server image or
 * its own blob: preview) and only reports picks/removals up. Picking works via
 * the file dialog or drag & drop (a multi-file drop keeps the first file).
 *
 * Purely presentational: previews and any server upload are the caller's job
 * (`uploading` only dims the control while the caller works). Picks larger
 * than `maxBytes` (1 MB by default) never reach `onPick` — they're reported
 * via `onRejected` so the caller can surface the error.
 */
export function ImageUpload({
  imageUrl,
  uploading = false,
  title = 'Drag & Drop File Here',
  description = 'Drag and drop your PNG, JPG, WebP, SVG images here or browse',
  browseLabel = 'Browse File',
  uploadingLabel = 'Uploading…',
  editLabel = 'Change',
  removeLabel,
  label,
  accept = 'image/*',
  maxBytes = MAX_IMAGE_BYTES,
  onRejected,
  disabled = false,
  id,
  describedBy,
  invalid = false,
  onPick,
  onRemove,
  className,
  variant = 'default',
}: ImageUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);
  const busy = disabled || uploading;

  const openPicker = (): void => {
    inputRef.current?.click();
  };

  const guardSize = (file: File): boolean => {
    if (file.size > maxBytes) {
      onRejected?.([file.name]);
      return false;
    }
    return true;
  };

  const handleDropFiles = (list: FileList | null): void => {
    const file = list?.[0];
    if (file && guardSize(file)) onPick(file);
  };

  return (
    <div className={cn('grid gap-2', className)}>
      {label ? <span className="px-1 text-sm font-medium">{label}</span> : null}

      <div className={cn(uploading && 'cursor-wait opacity-60')}>
        {imageUrl ? (
          <DropzoneFilledFace
            previewSrc={imageUrl}
            disabled={busy}
            editLabel={editLabel}
            removeLabel={removeLabel}
            variant={variant}
            onReplace={openPicker}
            onRemove={onRemove}
          />
        ) : (
          <DropzoneEmptyFace
            title={variant !== 'default' && uploading ? uploadingLabel : title}
            description={description}
            browseLabel={uploading ? uploadingLabel : browseLabel}
            dragging={dragging}
            disabled={busy}
            invalid={invalid}
            variant={variant}
            {...(describedBy !== undefined ? { describedBy } : {})}
            onOpen={openPicker}
            onDraggingChange={setDragging}
            onDropFiles={handleDropFiles}
          />
        )}
      </div>

      <input
        ref={inputRef}
        id={id}
        type="file"
        className="sr-only"
        accept={accept}
        disabled={busy}
        // The visible dropzone button is the primary control; the input still
        // needs a name for callers without label association (it mirrors the
        // visible browse action, so label-in-name holds).
        {...(typeof browseLabel === 'string' ? { 'aria-label': browseLabel } : {})}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file && guardSize(file)) onPick(file);
        }}
      />
    </div>
  );
}
